import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, HStack, Flex } from '@chakra-ui/react';
import { FaPlay, FaPause } from 'react-icons/fa';

const WaveformAudioPlayer = ({ src, type = 'audio/wav', isMobile = false }) => {
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [waveformData, setWaveformData] = useState([]);

  // Generate waveform data (simulated - can be replaced with real audio analysis)
  const generateWaveformData = (numBars = 50) => {
    const data = [];
    for (let i = 0; i < numBars; i++) {
      // Create a waveform that peaks in the middle (like the image)
      const normalizedPos = i / numBars;
      let amplitude;
      
      if (normalizedPos < 0.3) {
        // Increasing from left
        amplitude = 0.2 + (normalizedPos / 0.3) * 0.6;
      } else if (normalizedPos < 0.7) {
        // Peak in middle
        amplitude = 0.8 + (Math.random() * 0.2);
      } else {
        // Slightly decreasing but still high
        amplitude = 0.7 + (Math.random() * 0.2);
      }
      
      data.push(amplitude);
    }
    return data;
  };

  const drawWaveform = (progress = 0) => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    
    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    
    const barWidth = (canvas.width / dpr) / waveformData.length;
    const centerY = (canvas.height / dpr) / 2;
    const maxHeight = (canvas.height / dpr) * 0.75;
    const progressIndex = Math.floor(progress * waveformData.length);
    
    waveformData.forEach((amplitude, index) => {
      const barHeight = amplitude * maxHeight;
      const x = index * barWidth;
      const barSpacing = 1;
      
      // Color based on progress
      if (index <= progressIndex) {
        ctx.fillStyle = '#1E90FF'; // Bright blue for played
      } else {
        ctx.fillStyle = '#87CEEB'; // Light blue for unplayed
      }
      
      // Draw vertical bar (symmetrical) with rounded edges effect
      const barX = x + barSpacing;
      const barY = centerY - barHeight / 2;
      const barW = barWidth - barSpacing * 2;
      const barH = barHeight;
      const radius = 1;
      
      ctx.beginPath();
      ctx.moveTo(barX + radius, barY);
      ctx.lineTo(barX + barW - radius, barY);
      ctx.quadraticCurveTo(barX + barW, barY, barX + barW, barY + radius);
      ctx.lineTo(barX + barW, barY + barH - radius);
      ctx.quadraticCurveTo(barX + barW, barY + barH, barX + barW - radius, barY + barH);
      ctx.lineTo(barX + radius, barY + barH);
      ctx.quadraticCurveTo(barX, barY + barH, barX, barY + barH - radius);
      ctx.lineTo(barX, barY + radius);
      ctx.quadraticCurveTo(barX, barY, barX + radius, barY);
      ctx.closePath();
      ctx.fill();
    });
  };

  useEffect(() => {
    // Initial waveform draw and resize handler
    const redrawWaveform = () => {
      if (waveformData.length > 0 && audioRef.current) {
        const progress = audioRef.current.currentTime / (audioRef.current.duration || 1);
        drawWaveform(progress);
      } else if (waveformData.length > 0) {
        drawWaveform(0);
      }
    };

    // Initial draw
    redrawWaveform();

    // Handle window resize
    const handleResize = () => {
      setTimeout(redrawWaveform, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [waveformData]);

  useEffect(() => {
    const data = generateWaveformData(isMobile ? 40 : 60);
    setWaveformData(data);
  }, [isMobile]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => {
      setCurrentTime(audio.currentTime);
      
      // Update waveform visualization based on playback progress
      if (canvasRef.current && isPlaying && audio.duration) {
        const progress = audio.currentTime / audio.duration;
        updateWaveformProgress(progress);
      }
      
      if (isPlaying) {
        animationFrameRef.current = requestAnimationFrame(updateTime);
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      updateWaveformProgress(0);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    
    if (isPlaying) {
      animationFrameRef.current = requestAnimationFrame(updateTime);
    }

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, duration]);

  const updateWaveformProgress = (progress) => {
    drawWaveform(progress);
  };

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    // Don't seek if clicking on the button
    if (e.target.closest('button')) return;
    
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    
    const waveformBox = e.currentTarget.querySelector('[data-waveform]') || e.currentTarget;
    const rect = waveformBox.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const progress = Math.max(0, Math.min(1, x / rect.width));
    audio.currentTime = progress * audio.duration;
    setCurrentTime(audio.currentTime);
    updateWaveformProgress(progress);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Box
      bg="gray.100"
      borderRadius="lg"
      p={isMobile ? 2 : 3}
      position="relative"
      cursor="pointer"
      onClick={handleSeek}
      _hover={{ bg: 'gray.150' }}
      transition="background-color 0.2s"
    >
      <audio ref={audioRef} src={src} type={type} preload="metadata" />
      
      <Flex align="center" gap={isMobile ? 2 : 3} data-waveform>
        {/* Play/Pause Button */}
        <IconButton
          icon={isPlaying ? <FaPause size={isMobile ? 12 : 14} /> : <FaPlay size={isMobile ? 12 : 14} />}
          onClick={(e) => {
            e.stopPropagation();
            togglePlayPause();
          }}
          bg="white"
          color="blue.500"
          _hover={{ bg: 'blue.50', color: 'blue.600' }}
          borderRadius="full"
          aria-label={isPlaying ? 'Pause' : 'Play'}
          flexShrink={0}
          boxShadow="sm"
          border="1px solid"
          borderColor="gray.200"
          w={isMobile ? '44px' : '52px'}
          h={isMobile ? '44px' : '52px'}
          display="flex"
          alignItems="center"
          justifyContent="center"
        />

        {/* Waveform */}
        <Box 
          flex={1} 
          position="relative" 
          h={isMobile ? '32px' : '40px'}
          style={{
            filter: 'blur(0.5px)',
          }}
        >
          <canvas
            ref={canvasRef}
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
            }}
          />
        </Box>

        {/* Time Display */}
        <Box
          fontSize={isMobile ? 'xs' : 'sm'}
          color="gray.600"
          fontWeight="medium"
          minW={isMobile ? '45px' : '55px'}
          textAlign="right"
          flexShrink={0}
        >
          {formatTime(currentTime)} / {formatTime(duration)}
        </Box>
      </Flex>
    </Box>
  );
};

export default WaveformAudioPlayer;

